import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { usePaperTheme } from '@/hooks/usePaperTheme';
import { deleteCloudProject, listCloudProjects, loadCloudProject } from '@/services/api/cloudsync';
import type { CloudProject } from '@/types/cloudsync';
import { APIError } from '@/types/api';
import { useUpgradePromptStore } from '@/features/billing/upgradePromptStore';
import { useEditorStore } from '@/features/editor/store';

function timeAgo(ts: string): string {
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export function ProjectsPage() {
  usePaperTheme();
  const [projects, setProjects] = useState<CloudProject[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const loadDiagram = useEditorStore((s) => s.loadDiagram);
  const clearCanvas = useEditorStore((s) => s.clearCanvas);

  const handleNewDiagram = useCallback(() => {
    clearCanvas();
    navigate('/editor');
  }, [clearCanvas, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listCloudProjects();
      setProjects(list);
    } catch (err) {
      if (err instanceof APIError && err.status === 402) {
        useUpgradePromptStore.getState().show('cloud_sync');
      } else {
        toast.error('Could not load your cloud projects');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const handleOpen = useCallback(async (p: CloudProject) => {
    try {
      const resp = await loadCloudProject(p.id);
      loadDiagram(resp.diagram);
      // Carry the project identity into the editor — useAutosave reads
      // these on mount so the title bar opens on the correct name instead
      // of "Untitled Diagram", and so subsequent saves update *this*
      // project instead of forking a duplicate cloud entry.
      useEditorStore.getState().setPendingProject(resp.project.id, resp.project.title);
      navigate('/editor');
    } catch {
      toast.error('Failed to load project');
    }
  }, [loadDiagram, navigate]);

  const handleDelete = useCallback(async (p: CloudProject) => {
    if (!confirm(`Delete "${p.title}"? This removes it from the cloud.`)) return;
    try {
      await deleteCloudProject(p.id);
      setProjects((prev) => prev.filter((r) => r.id !== p.id));
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  }, []);

  return (
    <AppLayout>
      <main className="flex-1 overflow-y-auto px-6 py-8">
        <div className="mx-auto max-w-4xl">
          <PageHeader
            title="My Projects"
            description="Diagrams saved to your account. Local IndexedDB is still the default."
            backTo="/dashboard"
            actions={
              <button
                onClick={handleNewDiagram}
                className="rounded-clyra border border-line-default px-3 py-1.5 text-tiny font-medium text-ink-secondary hover:text-ink-primary hover:border-line-strong transition-colors"
              >
                New diagram
              </button>
            }
          />

          {loading ? (
            <div className="mt-16 text-center text-sm text-ink-muted">Loading…</div>
          ) : projects.length === 0 ? (
            <div className="mt-8 rounded-clyra-lg border border-dashed border-line-default p-12 text-center">
              <p className="text-sm text-ink-secondary">No cloud-saved projects yet</p>
              <p className="mt-1 text-petit text-ink-muted">
                Use “Save to cloud” in the editor to keep a snapshot accessible from any device.
              </p>
            </div>
          ) : (
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {projects.map((p) => (
                <div key={p.id} className="rounded-clyra-lg border border-line-default bg-clyra-raised/60 p-4 transition-colors hover:border-line-strong">
                  {p.preview_url && (
                    <div className="mb-3 h-28 overflow-hidden rounded-clyra border border-line-subtle bg-clyra-bg">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={p.preview_url} alt={p.title} className="h-full w-full object-cover" />
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink-primary truncate">{p.title}</p>
                      <p className="mt-0.5 text-micro text-ink-faint">
                        Updated {timeAgo(p.updated_at)} · {formatBytes(p.byte_size)} · {p.storage === 's3' ? 'cloud' : 'server'}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        onClick={() => handleOpen(p)}
                        className="rounded-clyra bg-brand-500/15 px-2.5 py-1 text-tiny font-medium text-brand-400 hover:bg-brand-500/25 transition-colors"
                      >
                        Open
                      </button>
                      <button
                        onClick={() => handleDelete(p)}
                        className="text-tiny text-danger/60 hover:text-danger transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </AppLayout>
  );
}
